import { DAY, MINUTE, STORAGE_KEY } from './constants';

const ACTIONS = {
  finder: [
    'finderActivity',
    'calendarOpened',
    'occupationOpened',
    'placeOpened',
    'promocodeOpened',
    'calendarChanged',
    'occupationChanged',
    'placeChanged',
    'promocodeChanged',
    'searchSubmitted',
  ],
  rates: [
    'ratesActivity',
    'roomInfo',
    'mediaViewed',
    'priceDetails',
    'cancellationInfo',
    'offerInfo',
    'cancellationChanged',
    'rateChanged',
    'boardChanged',
    'filtersChanged',
    'roomSelected',
    'roomRemoved',
    'extrasChanged',
    'extrasSkipped',
    'extrasSubmitted',
  ],
  checkout: ['paymentChanged'],
};

const createJourney = (scope, now, clock) => {
  const key = `${STORAGE_KEY}:${scope}:journey`;
  const state = { stages: {}, actions: {}, recent: [], browserRecent: [] };
  let stage;
  let since;
  let visible = false;
  let lastActionAt;
  let available = true;
  const count = (value) => Number.isInteger(value) && value >= 0 && value <= 10000;
  const duration = (value) => Number.isFinite(value) && value >= 0 && value <= DAY;
  const validAction = (name) => Object.values(ACTIONS).some((names) => names.includes(name));
  try {
    const raw = sessionStorage.getItem(key);
    if (raw && raw.length <= 32768) {
      const saved = JSON.parse(raw);
      if (saved.version === 1 && saved.at <= now() && saved.at > now() - 30 * MINUTE) {
        Object.keys(ACTIONS).forEach((name) => {
          const value = saved.stages?.[name];
          if (value && duration(value.visibleMs) && count(value.entries))
            state.stages[name] = { visibleMs: value.visibleMs, entries: value.entries };
        });
        Object.values(ACTIONS)
          .flat()
          .forEach((name) => {
            const value = saved.actions?.[name];
            if (value && ['count', 'browser', 'synthetic', 'unknown'].every((field) => count(value[field])))
              state.actions[name] = Object.fromEntries(
                ['count', 'browser', 'synthetic', 'unknown'].map((field) => [field, value[field]]),
              );
          });
        state.recent = (Array.isArray(saved.recent) ? saved.recent : [])
          .slice(-20)
          .filter((item) => item && validAction(item.action) && item.at <= now() && item.at > now() - 30 * MINUTE)
          .map(({ action, at }) => ({ action, at }));
        state.browserRecent = (Array.isArray(saved.browserRecent) ? saved.browserRecent : [])
          .slice(-20)
          .filter(
            (item) =>
              item && ['finder', 'rates'].includes(item.stage) && item.at <= now() && item.at > now() - 30 * MINUTE,
          )
          .map(({ stage: name, at }) => ({ stage: name, at }));
        const { lastActionAt: savedActionAt } = saved;
        if (savedActionAt <= now() && savedActionAt > now() - 30 * MINUTE) lastActionAt = savedActionAt;
      }
    }
  } catch {
    available = false;
  }
  const flush = () => {
    if (stage && since !== undefined) {
      state.stages[stage].visibleMs = Math.min(DAY, state.stages[stage].visibleMs + Math.max(0, clock() - since));
      since = visible ? clock() : undefined;
    }
  };
  const persist = () => {
    flush();
    try {
      sessionStorage.setItem(key, JSON.stringify({ ...state, lastActionAt, at: now(), version: 1 }));
      available = true;
    } catch {
      available = false;
    }
  };
  const enter = (next) => {
    if (!Object.keys(ACTIONS).includes(next) || next === stage) return;
    flush();
    stage = next;
    state.stages[stage] ||= { visibleMs: 0, entries: 0 };
    state.stages[stage].entries = Math.min(10000, state.stages[stage].entries + 1);
    since = visible ? clock() : undefined;
    persist();
  };
  const action = ({ name, stage: next, trusted }) => {
    if (!Object.keys(ACTIONS).includes(next) || !ACTIONS[next].includes(name)) return;
    enter(next);
    const value = (state.actions[name] ||= { count: 0, browser: 0, synthetic: 0, unknown: 0 });
    const source = trusted === true ? 'browser' : trusted === false ? 'synthetic' : 'unknown';
    value.count = Math.min(10000, value.count + 1);
    value[source] = Math.min(10000, value[source] + 1);
    if (trusted === true) {
      lastActionAt = now();
      if (['finder', 'rates'].includes(next)) {
        state.browserRecent.push({ stage: next, at: now() });
        state.browserRecent = state.browserRecent.slice(-20);
      }
    }
    state.recent.push({ action: name, at: now() });
    state.recent = state.recent.slice(-20);
    persist();
  };
  return {
    enter,
    action,
    visibility: (next) => {
      flush();
      visible = next;
      since = visible && stage ? clock() : undefined;
      persist();
    },
    snapshot: () => {
      flush();
      return {
        ...JSON.parse(JSON.stringify(state)),
        currentStage: stage || null,
        lastBrowserActionAgeMs: lastActionAt === undefined ? null : Math.max(0, now() - lastActionAt),
        storageAvailable: available,
        source: 'scopedCallbacksAndSessionStorage',
      };
    },
  };
};

export { createJourney };
